import { action, computed, observable } from 'mobx'
import { validationContext, required } from 'validx'
import { ModalViewModel } from '@/infra/ModalViewModel'
import {
  mustBeValidEin,
  mustBeValidTaxFormInputValue,
  mustHaveMaxLength,
  when,
} from '@/utils/validx-util'
import { BusinessType } from '@taxfyle/txf-api-client'

export interface AddBusinessDialogViewModelOptions {
  onSaved: (business: NewBusinessData) => void
}

export interface NewBusinessData {
  businessType: BusinessType
  businessName: string | null
  ein: string | null
  principleBusiness: string | null
}

export class AddBusinessDialogViewModel {
  readonly modal = new ModalViewModel()

  @observable accessor businessType: BusinessType = BusinessType.LLC
  @observable accessor businessName = ''
  @observable accessor ein = ''
  @observable accessor principleBusiness = ''

  readonly validation = validationContext(this, {
    businessType: [required('Business type is required')],
    businessName: [
      when(() => this.isLLC, required('Business name is required')),
      mustBeValidTaxFormInputValue(),
      mustHaveMaxLength(90, 'Business name must not exceed 90 characters'),
    ],
    ein: [
      when(() => this.isLLC, required('EIN is required')),
      mustBeValidEin(),
    ],
    principleBusiness: [
      when(() => this.isSoleProp, required('Business activity is required')),
      mustBeValidTaxFormInputValue(),
      mustHaveMaxLength(90, 'Business activity must not exceed 90 characters'),
    ],
  })

  constructor(private readonly _options: AddBusinessDialogViewModelOptions) {}

  @computed
  get isLLC() {
    return this.businessType === BusinessType.LLC
  }

  @computed
  get isSoleProp() {
    return this.businessType === BusinessType.SoleProp
  }

  @action.bound
  reset() {
    this.businessType = BusinessType.LLC
    this.businessName = ''
    this.ein = ''
    this.principleBusiness = ''
    this.validation.reset()
  }

  @action.bound
  showDialog() {
    this.reset()
    this.modal.show()
  }

  @action.bound
  closeDialog() {
    this.modal.close()
  }

  @action.bound
  save() {
    this.validation.reset().validate()
    if (!this.validation.isValid) {
      return
    }

    const businessData: NewBusinessData = {
      businessType: this.businessType,
      businessName: this.businessName || null,
      ein: this.ein || null,
      principleBusiness: this.principleBusiness || null,
    }

    this._options.onSaved(businessData)
    this.closeDialog()
  }
}
